import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Card, Col, Row,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import './alert-form.css';

import {
  AlertFormSteps,
  clearStepData,
  createAlerts,
  resetForm,
  restoreFormState,
  selectAlertFormIsValid,
  selectAlertFormState,
  selectAlertFormStep,
  selectSensorsData,
  setAlertFormMode,
  setStepCompleted,
} from '../../../redux/slices/alertFormSlice/alertFormSlice';

import {
  validateDefineActionSection,
  validateDefineThresholdSection,
  validateNameAlertAndMoreSection,
  validateSelectSensorsSection,
} from './AlertFormValidation';

import SelectSensorsForm from './SelectSensorsSection/SelectSensorsForm';
import DefineThresholdsForm from './DefineThresholdSection/DefineThresholdsForm';
import DefineActionsForm from './DefineActionsSection/DefineActionsForm';
import NameAlertAndMoreForm from './NameAlertAndMoreSection/NameAlertAndMoreForm';
import AlertFormReviewLayout from './AlertFormReview/AlertFormReview';

import {
  clearAppliedChannelSearch,
  setAppliedChannelSearch,
} from '../../../redux/slices/channelListSlice/channelListSlice';

import { fetchFilteredAlertList } from '../../../redux/slices/alertListSlice/alertListSlice';
import SelectSensorsSummary from './SelectSensorsSection/SelectSensorsSummary';
import DefineThresholdsSummary from './DefineThresholdSection/DefineThresholdsSummary';
import DefineActionsSummary from './DefineActionsSection/DefineActionsSummary';
import NameAlertAndMoreSummary from './NameAlertAndMoreSection/NameAlertAndMoreSummary';
import { buildChannelTypeFiltersFromChannelKey, getIncompleteSteps } from './AlertFormUtility';

export const STEP_ORDER = [
  AlertFormSteps.SELECT_SENSORS,
  AlertFormSteps.DEFINE_THRESHOLD,
  AlertFormSteps.DEFINE_ACTIONS,
  AlertFormSteps.NAME_ALERT,
  AlertFormSteps.REVIEW,
];

const AlertFormWrapper = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const reduxCurrentStep = useSelector(selectAlertFormStep);
  const alertFormState = useSelector(selectAlertFormState);
  const canGoNext = useSelector(selectAlertFormIsValid);
  const { selectedChannels } = useSelector(selectSensorsData);

  const [currentStep, setCurrentStep] = useState(reduxCurrentStep || STEP_ORDER[0]);
  const [editingStep, setEditingStep] = useState(null);
  const preEditSnapshotRef = useRef(null);

  let nextButtonLabel = 'Next';
  if (editingStep) nextButtonLabel = 'Apply';
  else if (currentStep === STEP_ORDER[STEP_ORDER.length - 1]) nextButtonLabel = 'Create Alert(s)';

  // Initialization
  useEffect(() => {
    dispatch(fetchFilteredAlertList({ filters: [] }));

    const preloadedChannelKey = selectedChannels && selectedChannels[0];
    if (preloadedChannelKey) {
      const channelTypeFilters = buildChannelTypeFiltersFromChannelKey(preloadedChannelKey);
      dispatch(setAppliedChannelSearch({
        filters: channelTypeFilters.length > 0 ? channelTypeFilters : [],
      }));
    } else {
      dispatch(setAppliedChannelSearch({ filters: [] }));
    }

    dispatch(setAlertFormMode({ isEditing: false, alertId: null }));

    return () => {
      dispatch(resetForm());
      dispatch(clearAppliedChannelSearch());
    };
  }, [dispatch]);

  const goToNextStep = () => {
    let isValid;

    switch (currentStep) {
      case AlertFormSteps.SELECT_SENSORS:
        isValid = dispatch(validateSelectSensorsSection());
        break;
      case AlertFormSteps.DEFINE_THRESHOLD:
        isValid = dispatch(validateDefineThresholdSection());
        break;
      case AlertFormSteps.DEFINE_ACTIONS:
        isValid = dispatch(validateDefineActionSection());
        break;
      case AlertFormSteps.NAME_ALERT:
        isValid = dispatch(validateNameAlertAndMoreSection());
        break;
      default:
        isValid = true;
    }

    if (isValid) {
      if (editingStep) {
        setEditingStep(null);
        const incompleteSteps = getIncompleteSteps(alertFormState);
        if (incompleteSteps.length > 0) {
          const firstIncompleteStep = STEP_ORDER.find((step) => incompleteSteps.includes(step));
          if (firstIncompleteStep) {
            // navigate to the first incomplete step
            setCurrentStep(firstIncompleteStep);
            return;
          }
        }
        setCurrentStep(AlertFormSteps.REVIEW);
        preEditSnapshotRef.current = null;
        return;
      }

      dispatch(setStepCompleted({ step: currentStep, completed: true }));
      const currentIndex = STEP_ORDER.indexOf(currentStep);
      if (currentIndex < STEP_ORDER.length - 1) {
        setCurrentStep(STEP_ORDER[currentIndex + 1]);
      }
    }
  };

  const goToPreviousStep = () => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      dispatch(clearStepData({ step: currentStep }));
      setCurrentStep(STEP_ORDER[currentIndex - 1]);
    }
  };

  const cancelEdit = () => {
    if (preEditSnapshotRef.current) {
      dispatch(restoreFormState(preEditSnapshotRef.current));
    }
    setEditingStep(null);
    setCurrentStep(AlertFormSteps.REVIEW);
    preEditSnapshotRef.current = null;
  };

  const onCreate = () => {
    dispatch(createAlerts());
  };

  const goToStep = (step) => {
    if (!preEditSnapshotRef.current) preEditSnapshotRef.current = alertFormState;
    setEditingStep(step);
    setCurrentStep(step);
  };

  const renderStepSummaries = () => STEP_ORDER.map((step, index) => {
    if (STEP_ORDER.indexOf(currentStep) > index) {
      switch (step) {
        case AlertFormSteps.SELECT_SENSORS:
          return (
            <Row key={step} className="mt-3">
              <Col>
                <SelectSensorsSummary key={step} onEdit={null} />
              </Col>
            </Row>
          );
        case AlertFormSteps.DEFINE_THRESHOLD:
          return (
            <Row key={step} className="mt-3">
              <Col>
                <DefineThresholdsSummary key={step} onEdit={null} />
              </Col>
            </Row>
          );
        case AlertFormSteps.DEFINE_ACTIONS:
          return (
            <Row key={step} className="mt-3">
              <Col>
                <DefineActionsSummary key={step} onEdit={null} />
              </Col>
            </Row>
          );
        case AlertFormSteps.NAME_ALERT:
          return (
            <Row key={step} className="mt-3">
              <Col>
                <NameAlertAndMoreSummary key={step} onEdit={null} />
              </Col>
            </Row>
          );
        default:
          return null;
      }
    }
    return null;
  });

  // === Explicit step rendering ===
  const renderCurrentStepForm = () => {
    switch (currentStep) {
      case AlertFormSteps.SELECT_SENSORS:
        return <SelectSensorsForm />;
      case AlertFormSteps.DEFINE_THRESHOLD:
        return <DefineThresholdsForm />;
      case AlertFormSteps.DEFINE_ACTIONS:
        return <DefineActionsForm />;
      case AlertFormSteps.NAME_ALERT:
        return <NameAlertAndMoreForm />;
      default:
        return null;
    }
  };

  if (currentStep === AlertFormSteps.REVIEW) {
    return (
      <AlertFormReviewLayout
        onBack={goToPreviousStep}
        onCreate={onCreate}
        onClose={() => history.push('/s/alerts')}
        goToStep={goToStep}
      />
    );
  }

  return (
    <>
      <div className="d-flex align-items-center">
        <h2 className="mr-2 mb-0">Configure New Alerts</h2>
        <a
          className="text-primary mt-2"
          title="How to Create Alerts"
          target="_blank"
          rel="noopener noreferrer"
          href="https://avtech.com/articles/223/"
        >
          How to Create Alerts&nbsp;
          <FontAwesomeIcon icon={faQuestionCircle} size="xs" />
        </a>
      </div>

      <Row className="mt-3">
        <Col>
          {currentStep === AlertFormSteps.SELECT_SENSORS && selectedChannels.length === 0 && (
            <Card className="alert-card">
              <Card.Body>
                You may use this form to create a single alert, or to apply the same sensor
                threshold to multiple Room Alert sensors. If you select multiple sensors,
                Room Alert Account will create multiple alerts that all perform the same action(s)
                when triggered or cleared.
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <Row>
        <Col>{renderStepSummaries()}</Col>
      </Row>

      <Row className="mt-3">
        <Col>{renderCurrentStepForm()}</Col>
      </Row>

      <Row className="mt-3">
        <Col>
          {(currentStep !== STEP_ORDER[0] || editingStep) && (
            <Button
              variant="secondary"
              onClick={editingStep ? cancelEdit : goToPreviousStep}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              {editingStep ? 'Cancel' : 'Go Back'}
            </Button>
          )}
        </Col>
        <Col className="text-right">
          <Button variant="secondary" onClick={goToNextStep} disabled={!canGoNext}>
            {nextButtonLabel}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default AlertFormWrapper;
